import type { StudioPremiumFlowStage } from "../../lib/studio/studioPremiumDemo";
import type { VetCanStudioStepId } from "../../lib/studio/studioReadModel";

function stageTone(state: StudioPremiumFlowStage["state"], active: boolean) {
  if (active) {
    return {
      border: "1px solid rgba(41, 84, 103, 0.22)",
      background:
        "linear-gradient(180deg, rgba(240, 248, 250, 0.95), rgba(255, 252, 248, 0.98))",
      labelBackground: "rgba(41, 84, 103, 0.1)",
      labelColor: "#295467",
      dot: "#295467",
    };
  }

  if (state === "blocked") {
    return {
      border: "1px solid rgba(162, 62, 62, 0.16)",
      background:
        "linear-gradient(180deg, rgba(255, 245, 243, 0.95), rgba(255, 252, 248, 0.98))",
      labelBackground: "rgba(162, 62, 62, 0.12)",
      labelColor: "#812d2d",
      dot: "#a23e3e",
    };
  }

  if (state === "review") {
    return {
      border: "1px solid rgba(181, 141, 34, 0.16)",
      background:
        "linear-gradient(180deg, rgba(255, 250, 239, 0.95), rgba(255, 252, 248, 0.98))",
      labelBackground: "rgba(181, 141, 34, 0.16)",
      labelColor: "#6d5210",
      dot: "#8f6e16",
    };
  }

  return {
    border: "1px solid rgba(44, 93, 67, 0.14)",
    background:
      "linear-gradient(180deg, rgba(244, 250, 246, 0.95), rgba(255, 252, 248, 0.98))",
    labelBackground: "rgba(44, 93, 67, 0.12)",
    labelColor: "#214735",
    dot: "#2c5d43",
  };
}

export default function StudioFlowGuide(props: {
  stages: StudioPremiumFlowStage[];
  activeStepId: VetCanStudioStepId;
  onSelectStage: (stepId: VetCanStudioStepId) => void;
}) {
  return (
    <>
      <p style={{ margin: "1rem 0 0", color: "#5b534b", lineHeight: 1.65 }}>
        Use this install-day order: finish the draft in Studio, rehearse in
        Preview, read launch control as the safety check, then verify live truth
        in the Dashboard and integrations. If those checks regress, stop new
        edits and switch to the rollback plan.
      </p>

      <section
        aria-label="Studio guided flow"
        style={{
          marginTop: "1.15rem",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: "0.85rem",
        }}
      >
        {props.stages.map((stage, index) => {
          const active = stage.id === props.activeStepId;
          const tone = stageTone(stage.state, active);

          return (
            <button
              key={stage.id}
              type="button"
              onClick={() => props.onSelectStage(stage.id)}
              style={{
                textAlign: "left",
                borderRadius: 24,
                border: tone.border,
                background: tone.background,
                padding: "1rem",
                cursor: "pointer",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "0.75rem",
                  alignItems: "center",
                }}
              >
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.42rem",
                    borderRadius: 999,
                    padding: "0.25rem 0.55rem",
                    background: tone.labelBackground,
                    color: tone.labelColor,
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    textTransform: "capitalize",
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 999,
                      background: tone.dot,
                    }}
                  />
                  {active ? "Active" : stage.state}
                </span>
                <span
                  style={{
                    color: "#7b726a",
                    fontSize: "0.76rem",
                    fontWeight: 700,
                  }}
                >
                  {index + 1}
                </span>
              </div>
              <div
                style={{
                  marginTop: "0.75rem",
                  fontSize: "1rem",
                  fontWeight: 700,
                  color: "#26221d",
                }}
              >
                {stage.label}
              </div>
              <p style={{ margin: "0.45rem 0 0", color: "#5b534b", lineHeight: 1.55 }}>
                {stage.detail}
              </p>
            </button>
          );
        })}
      </section>
    </>
  );
}
